/**
 * DashboardGamification — progress bar, streak tracker, and badges.
 *
 * Sits in the sidebar column of DashboardOverview, giving users
 * a reason to return daily even without a new job offer.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

/* ── Types ── */
export interface Badge {
  id: string;
  emoji: string;
  labelKey: string;
  fallback: string;
  earned: boolean;
}

interface DashboardGamificationProps {
  profileReadyPct: number;
  streakDays: number;
  totalApplications: number;
  totalInterviews: number;
  actionsToNext?: number;
  nextMilestonePct?: number;
}

const PROGRESS_BLOCKS = 15;

function buildBadges(
  streakDays: number,
  totalApps: number,
  totalInterviews: number,
  profilePct: number,
): Badge[] {
  return [
    {
      id: 'resume-ready',
      emoji: '🏆',
      labelKey: 'dashboard.gamification.badgeResumeReady',
      fallback: 'Resume Ready',
      earned: profilePct >= 80,
    },
    {
      id: '10-apps',
      emoji: '🎯',
      labelKey: 'dashboard.gamification.badge10Apps',
      fallback: '10 Applications',
      earned: totalApps >= 10,
    },
    {
      id: '7-day-streak',
      emoji: '🔥',
      labelKey: 'dashboard.gamification.badge7DayStreak',
      fallback: '7-Day Streak',
      earned: streakDays >= 7,
    },
    {
      id: 'first-interview',
      emoji: '💼',
      labelKey: 'dashboard.gamification.badgeFirstInterview',
      fallback: 'First Interview',
      earned: totalInterviews >= 1,
    },
    {
      id: '50-apps',
      emoji: '🚀',
      labelKey: 'dashboard.gamification.badge50Apps',
      fallback: '50 Applications',
      earned: totalApps >= 50,
    },
  ];
}

function BlockProgress({ pct }: { pct: number }) {
  const clamped = Math.min(100, Math.max(0, pct));
  const filled = Math.round((clamped / 100) * PROGRESS_BLOCKS);

  return (
    <div
      className="ds-gamification-blocks"
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`${clamped}% profile ready`}
    >
      {Array.from({ length: PROGRESS_BLOCKS }, (_, i) => (
        <span
          key={i}
          className={`ds-gamification-blocks__cell${i < filled ? ' ds-gamification-blocks__cell--filled' : ''}`}
        />
      ))}
    </div>
  );
}

const DashboardGamification: React.FC<DashboardGamificationProps> = ({
  profileReadyPct,
  streakDays,
  totalApplications,
  totalInterviews,
  actionsToNext = 3,
  nextMilestonePct = 85,
}) => {
  const { t } = useTranslation();
  const badges = buildBadges(streakDays, totalApplications, totalInterviews, profileReadyPct);
  const earnedCount = badges.filter((b) => b.earned).length;
  const responseRate = totalApplications > 0
    ? Math.round((totalInterviews / totalApplications) * 100)
    : 0;

  return (
    <section className="ds-dashboard-gamification" aria-label={t('dashboard.gamification.profileProgress', 'Job Search Progress')}>
      {/* Progress */}
      <div className="ds-dashboard-gamification__section">
        <p className="ds-dashboard-gamification__heading">
          {t('dashboard.gamification.profileProgress', 'Job Search Progress')}
        </p>
        <div className="ds-dashboard-gamification__pct-row">
          <span className="ds-dashboard-gamification__pct t-num">{profileReadyPct}%</span>
          <span className="ds-dashboard-gamification__pct-label">
            {t('dashboard.gamification.profileReady', 'Profile Ready')}
          </span>
        </div>
        <BlockProgress pct={profileReadyPct} />
        {profileReadyPct < nextMilestonePct && actionsToNext > 0 ? (
          <p className="ds-dashboard-gamification__hint">
            {t('dashboard.gamification.completeActions', {
              count: actionsToNext,
              pct: nextMilestonePct,
              defaultValue: `Complete ${actionsToNext} actions to reach ${nextMilestonePct}%.`,
            })}
          </p>
        ) : (
          <p className="ds-dashboard-gamification__hint">
            {t(
              'dashboard.gamification.profileReadyDone',
              'Profile is in great shape — keep the streak going.',
            )}
          </p>
        )}
      </div>

      {/* Streak + stats */}
      <div className="ds-dashboard-gamification__section ds-dashboard-gamification__section--divider">
        <p className="ds-dashboard-gamification__streak">
          <span aria-hidden>🏆</span>
          {t('dashboard.gamification.streakTitle', {
            days: streakDays,
            defaultValue: `${streakDays}-day job search streak`,
          })}
        </p>
        <ul className="ds-dashboard-gamification__stats">
          <li>
            <span className="ds-dashboard-gamification__stat-label">
              {t('dashboard.gamification.statApps', 'Applications')}:
            </span>
            <span className="ds-dashboard-gamification__stat-value t-num">{totalApplications}</span>
          </li>
          <li>
            <span className="ds-dashboard-gamification__stat-label">
              {t('dashboard.gamification.statInterviews', 'Interviews')}:
            </span>
            <span className="ds-dashboard-gamification__stat-value t-num">{totalInterviews}</span>
          </li>
          <li>
            <span className="ds-dashboard-gamification__stat-label">
              {t('dashboard.gamification.statResponseRate', 'Response rate')}:
            </span>
            <span className="ds-dashboard-gamification__stat-value t-num">
              {totalApplications > 0 ? `${responseRate}%` : '—'}
            </span>
          </li>
        </ul>
      </div>

      {/* Badges */}
      <div className="ds-dashboard-gamification__section ds-dashboard-gamification__section--divider">
        <div className="ds-dashboard-gamification__badges-header">
          <p className="ds-dashboard-gamification__heading">
            {t('dashboard.gamification.badges', 'Badges')}
          </p>
          <span className="ds-dashboard-gamification__badges-count t-num">
            {earnedCount}/{badges.length}
          </span>
        </div>
        <ul className="ds-dashboard-gamification__badge-list">
          {badges.map((b) => (
            <li key={b.id}>
              <div
                className={`ds-dashboard-badge${b.earned ? ' ds-dashboard-badge--earned' : ''}`}
                title={t(b.labelKey, b.fallback)}
              >
                <span className="ds-dashboard-badge__emoji" aria-hidden>{b.emoji}</span>
                <span className="ds-dashboard-badge__label">
                  {t(b.labelKey, b.fallback)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default React.memo(DashboardGamification);
